/**
 * 房间码的客户端口径：归一化、逐字符校验、粘贴清洗、念给朋友听的分组。
 *
 * ★ 字母表是服务端 `packages/server/src/room/roomCode.ts` 的**镜像**。
 *   它不在 `@poker/shared` 里（见 deviations：本该放 shared），客户端又不能 import server，
 *   所以这里抄一份并用 `ROOM_CODE_ALPHABET_SOURCE` 注明来源。两边漂移的后果是
 *   「本地说码非法、服务端其实认」——所以下面只把字母表用于**提示与本地预检**，
 *   最终判定永远以服务端的 `E_ROOM_CODE_INVALID` 为准。
 *
 * ★ 关于「自动纠正」：任务书写的是「用户打了 O 就自动纠成 0」，但服务端字母表里
 *   **O 和 0 都不存在**（形近对的双方一起被剔除了），纠成 0 只会得到另一个非法字符。
 *   正确的做法是**拒绝该字符并给出具体提示**：把 O 误读出来的真码多半是 Q 或 D。
 *   见 deviations。
 */

/** 与服务端逐字符一致的 23 字符表。3 4 6 7 9 + 18 个字母。 */
export const ROOM_CODE_ALPHABET = '34679ACDEFHJKMNPQRTVWXY'
export const ROOM_CODE_ALPHABET_SOURCE = 'packages/server/src/room/roomCode.ts'
export const ROOM_CODE_LENGTH = 6

const ALPHABET = new Set(ROOM_CODE_ALPHABET)

/**
 * 形近字符 → 具体提示。
 * 提示要说「你看到的多半是什么」，而不是干巴巴一句「非法字符」——
 * 用户是照着别人截图/口述输入的，他需要知道该往哪个方向猜。
 */
const CONFUSABLE_HINTS: Readonly<Record<string, string>> = {
  O: '房间码里没有 O，多半是 Q 或 D',
  '0': '房间码里没有 0，多半是 Q 或 D',
  I: '房间码里没有 I，多半是 J 或 T',
  L: '房间码里没有 L，多半是 J 或 T',
  '1': '房间码里没有 1，多半是 J 或 T',
  S: '房间码里没有 S 和 5',
  '5': '房间码里没有 5 和 S',
  B: '房间码里没有 B 和 8',
  '8': '房间码里没有 8 和 B',
  G: '房间码里没有 G，多半是 6',
  Z: '房间码里没有 Z 和 2',
  '2': '房间码里没有 2 和 Z',
  U: '房间码里没有 U，多半是 V',
}

/** 这个字符能不能进房间码。入参应已大写。 */
export function isRoomCodeChar(ch: string): boolean {
  return ALPHABET.has(ch)
}

/**
 * 针对被拒字符的具体提示；没有专门提示就返回通用一句。
 * 只在「用户确实打了一个字母或数字」时调用——空格、横杠这类分隔符要静默丢掉。
 */
export function confusableHint(ch: string): string {
  return CONFUSABLE_HINTS[ch.toUpperCase()] ?? `房间码里没有「${ch}」`
}

/** 输入清洗的结果。`hint` 非 null 时应当轻提示一下（不是错误，别打断输入）。 */
export interface CodeInputResult {
  /** 已接受的字符，全大写，长度 ≤ ROOM_CODE_LENGTH */
  readonly code: string
  /** 被丢掉的字母/数字（分隔符不算） */
  readonly rejected: readonly string[]
  /** 第一个被拒字符对应的具体提示 */
  readonly hint: string | null
  /** 有没有因为超过 6 位而截断 */
  readonly truncated: boolean
}

/**
 * 把任意一段用户输入（键入、粘贴、扫码结果）接进当前码里。
 *
 * - 大小写不敏感：一律转大写
 * - 去掉一切非字母数字（空格、横杠、全角分隔符、emoji）
 * - 全角字母数字先折半角，否则「ＡＢＣ」会被整段吃掉
 * - 不在字母表里的字母/数字**丢弃并记账**，由调用方轻提示
 * - 超出 6 位的部分截断
 */
export function acceptCodeInput(raw: string, existing = ''): CodeInputResult {
  const rejected: string[] = []
  let code = existing.toUpperCase().slice(0, ROOM_CODE_LENGTH)
  let truncated = false

  for (const ch of toHalfWidth(raw).toUpperCase()) {
    if (!/[0-9A-Z]/.test(ch)) continue // 分隔符：静默丢掉，这是最常见的粘贴噪音
    if (!ALPHABET.has(ch)) {
      rejected.push(ch)
      continue
    }
    if (code.length >= ROOM_CODE_LENGTH) {
      truncated = true
      continue
    }
    code += ch
  }

  const first = rejected[0]
  return {
    code,
    rejected,
    hint: first === undefined ? null : confusableHint(first),
    truncated,
  }
}

/** 全角字母数字 → 半角。微信里长按复制常常带回全角。 */
function toHalfWidth(s: string): string {
  let out = ''
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0
    out += cp >= 0xff01 && cp <= 0xff5e ? String.fromCodePoint(cp - 0xfee0) : ch
  }
  return out
}

/**
 * 完整校验：与服务端 `normalizeRoomCode` 同口径。
 * 合法返回大写 6 位码，否则 null。
 */
export function normalizeRoomCode(raw: string): string | null {
  const cleaned = toHalfWidth(raw)
    .trim()
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toUpperCase()
  if (cleaned.length !== ROOM_CODE_LENGTH) return null
  for (const ch of cleaned) {
    if (!ALPHABET.has(ch)) return null
  }
  return cleaned
}

export function isValidRoomCode(raw: string): boolean {
  return normalizeRoomCode(raw) !== null
}

/**
 * 本地预检的具体失败原因。**只用于提示**，不代表服务端会怎么判。
 * 返回 null = 形状没问题，可以发出去。
 */
export function roomCodeProblem(raw: string): string | null {
  const cleaned = toHalfWidth(raw)
    .trim()
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toUpperCase()
  if (cleaned.length === 0) return '先输入 6 位房间码'
  for (const ch of cleaned) {
    if (!ALPHABET.has(ch)) return confusableHint(ch)
  }
  if (cleaned.length < ROOM_CODE_LENGTH) return `还差 ${ROOM_CODE_LENGTH - cleaned.length} 位`
  if (cleaned.length > ROOM_CODE_LENGTH) return '房间码是 6 位，多了'
  return null
}

/** 6 个格子的内容；不足补空串。渲染层直接 map 这个数组。 */
export function codeCells(code: string): string[] {
  const cells: string[] = []
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) cells.push(code[i] ?? '')
  return cells
}

/**
 * 念给朋友听 / 截图用的分组：`QK37DF` → `QK3 7DF`。
 * 三位一组是电话号码式的记忆分组，口述时不容易串行。
 */
export function groupRoomCode(code: string): string {
  const up = code.toUpperCase()
  if (up.length !== ROOM_CODE_LENGTH) return up
  return `${up.slice(0, 3)} ${up.slice(3)}`
}

/**
 * 邀请链接。★ 必须带 roomId：码有 7 天冷却后会被复用，
 * 只带 code 的旧链接会把人导进一个复用了同一码的陌生房间（与服务端 `inviteLink` 同口径）。
 */
export function inviteLink(origin: string, roomId: string, code: string): string {
  const base = origin.endsWith('/') ? origin.slice(0, -1) : origin
  return `${base}/join?room=${encodeURIComponent(roomId)}&code=${encodeURIComponent(code.toUpperCase())}`
}

/** 从 URL（`?code=` / `?room=` / `/join/XXXXXX`）里捞房间码与房间 id。 */
export function parseInviteTarget(href: string): { code: string | null; roomId: string | null } {
  let code: string | null = null
  let roomId: string | null = null
  try {
    const url = new URL(href)
    code = normalizeRoomCode(url.searchParams.get('code') ?? '')
    const room = url.searchParams.get('room')
    roomId = room !== null && room.startsWith('rm_') ? room : null
    if (code === null) {
      const tail = url.pathname.split('/').filter((s) => s !== '')
      const last = tail[tail.length - 1]
      if (last !== undefined) code = normalizeRoomCode(last)
    }
  } catch {
    return { code: null, roomId: null }
  }
  return { code, roomId }
}
